import {useEffect, useState} from "react";
import { format } from 'date-fns';
import apiService from "../ApiCallService";
import ErrorUtils from "../../utils/ErrorUtils";
import { Journal, JournalLine } from "src/models/calculation-builder/Journal";
import FormattingService from "../utils/FormattingService";
import { DryRunResponse } from "src/models/calculation-builder/DryRunResponse";
import CONSTANTS, {JE_ERROR_FIELD_MAPPING} from "src/utils/constants";

/**
 * This class should only contain the API calls related to JE Settings and Review Journals
 */
 export default class JournalsService {
    getJournals(getJournals: boolean | number, journalLineType: string, calculationNumber?: string, version?: number) : [Journal[], boolean, string] {
        const [result, setResult] = useState([] as Journal[]);
        const [loading, setLoading] = useState(false);
        const [error, setError] = useState('');

        useEffect(() => {
            async function fetchData($this: JournalsService, calculationNumber: string, version: number) {
                try {
                    setLoading(true);
                    const response = await apiService.getJournals(calculationNumber, version, journalLineType);
                    const json = await response.json();
                    const journals = json.journals;
                    $this.convertJournalFieldsToDisplayFormat(journals);
                    $this.convertLineErrorsToMap(journals);
                    setResult(journals);
                }
                catch (ex) {
                    setError(ErrorUtils.getMessage(ex));
                }
                finally {
                    setLoading(false);
                }
            }
            if (getJournals && calculationNumber != null && version != null) {
                fetchData(this, calculationNumber, version);
            }
        }, [getJournals, calculationNumber, version]);
        return [result, loading, error]
    }

    getJournalsV2(getJournals: number | boolean, journalLineType: string, calculationNumber?: string, version?: number) : [Journal[], boolean, string] {
        const [result, setResult] = useState([] as Journal[]);
        const [loading, setLoading] = useState(false);
        const [error, setError] = useState('');

        useEffect(() => {
            async function fetchData($this: JournalsService, calculationNumber: string, version: number) {
                try {
                    setLoading(true);
                    const response = await apiService.getJournalsV2(calculationNumber, version, journalLineType);
                    const json = await response.json();

                    // Fetch data from S3 URL
                    const s3Response = await fetch(json.s3DownloadUrl);
                    const journals = await s3Response.json();
                    $this.convertJournalFieldsToDisplayFormat(journals);
                    $this.convertLineErrorsToMap(journals);
                    setResult(journals);
                }
                catch (ex) {
                    setError(ErrorUtils.getMessage(ex));
                }
                finally {
                    setLoading(false);
                }
            }
            if (getJournals && calculationNumber != null && version != null) {
                fetchData(this, calculationNumber, version);
            }
        }, [getJournals, calculationNumber, version]);
        return [result, loading, error]
    }

    downloadJournals(downloadJournals: boolean, journalLineType?: string, calculationNumber?: string, version?: number) : [string, boolean, string]  {
        const [result, setResult] =  useState(null as unknown as string);
        const [loading, setLoading] = useState(false);
        const [error, setError] = useState('');
        
        useEffect(() => {
            async function fetchData(journalLineType: string, calculationNumber: string, version: number) {
                try {
                    setLoading(true);
                    const response = await apiService.downloadJournals(calculationNumber, version, journalLineType);
                    const json = await response.json();
                    if (json && json.downloadUrl) {
                        setResult(json.downloadUrl);
                    } else {
                        setError('Response missing downloadUrl property');
                    }
                }
                catch (ex) {
                    setError(ErrorUtils.getMessage(ex));
                }
                finally {
                    setLoading(false);
                }
            }
            if (downloadJournals && calculationNumber != null && version != null && journalLineType != null) {
                fetchData(journalLineType, calculationNumber, version);
            }
        }, [downloadJournals, calculationNumber, version, journalLineType]);
        return [result, loading, error]
    }

    uploadJETemplate(file: File, s3Key: string, calculationNumber?: string) : [string, boolean, string]  {
        const [result, setResult] =  useState(null as unknown as string);
        const [loading, setLoading] = useState(false);
        const [error, setError] = useState('');
        
        useEffect(() => {
            async function uploadData(s3Key: string, calculationNumber: string) {
                try {
                    setLoading(true);
                    await apiService.uploadToS3(CONSTANTS.ENVIRONMENT_VARIABLES.JE_TEMPLATE_UPLOADS_S3_BUCKET, s3Key, file);
                    const response = await apiService.uploadJETemplate(s3Key, calculationNumber);
                    setResult((await response.json()).s3Key as string);
                    setError('')
                }
                catch (ex) {
                    setError(ErrorUtils.getMessage(ex));
                }
                finally {
                    setLoading(false);
                }
            }
            if (s3Key && calculationNumber) {
                uploadData(s3Key, calculationNumber);
            }
        }, [s3Key, calculationNumber]);
        
        return [result, loading, error]
    }
    
    updateJournals(payload?: Journal[], calculationNumber?: string) : [Journal[], boolean, string] {
        const [result, setResult] = useState([] as Journal[]);
        const [loading, setLoading] = useState(false);
        const [error, setError] = useState('');

        useEffect(() => {
            async function postData($this: JournalsService, calculationNumber: string, payload: Journal[]) {
                try {
                    setLoading(true);
                    const response = await apiService.updateJournals(calculationNumber, {'journals': payload});
                    const json = await response.json();
                    const updatedJournals = json.journals;
                    $this.convertJournalFieldsToDisplayFormat(updatedJournals);
                    setResult(updatedJournals);
                }
                catch (ex) {
                    setError(ErrorUtils.getMessage(ex));
                }
                finally {
                    setLoading(false);
                }
            }
            if (calculationNumber != null && payload != null) {
                postData(this, calculationNumber, payload);
            }
        }, [payload]);
        return [result, loading, error]
    }

    dryRunCalculation(query: string, calculationNumber: string, version: number, generateTaxJournals: boolean) : [DryRunResponse, boolean, string] {
        const [result, setResult] = useState(null as unknown as DryRunResponse);
        const [loading, setLoading] = useState(false);
        const [error, setError] = useState('');

        useEffect(() => {
            async function postData($this: JournalsService, calculationNumber: string, version: number, generateTaxJournals: boolean) {
                try {
                    setLoading(true);
                    const response = await apiService.dryRunCalculation(calculationNumber, version, generateTaxJournals);
                    const dryRunResponse: DryRunResponse = await response.json();
                    $this.convertJournalFieldsToDisplayFormat(dryRunResponse.journals);
                    $this.convertLineErrorsToMap(dryRunResponse.journals);
                    setResult(dryRunResponse);
                }
                catch (ex) {
                    setError(ErrorUtils.getMessage(ex));
                }
                finally {
                    setLoading(false);
                }
            }
            if (query != null && calculationNumber != null && version != null && generateTaxJournals != null) {
                postData(this, calculationNumber, version, generateTaxJournals);
            }
        }, [query]);
        return [result, loading, error]
    }

    getJournalEntries(calculationExecutionId?: string) : [JournalLine[], JournalLine[], boolean, string] {
        const [result, setResult] = useState([] as JournalLine[]);
        const [reversalsResult, setReversalsResult] = useState([] as JournalLine[]);
        const [loading, setLoading] = useState(false);
        const [error, setError] = useState('');

        useEffect(() => {
            async function fetchData($this: JournalsService, calculationExecutionId: string) {
                try {
                    setLoading(true);
                    const response = await apiService.getJournalEntries(calculationExecutionId);
                    const json = await response.json();
                    const journalLines = json.journalLines;
                    const reversalJournalLines = json.reversalJournalLines || [];
                    $this.convertJournalLineFieldsToDisplayFormat(journalLines);
                    $this.convertJournalLineFieldsToDisplayFormat(reversalJournalLines);
                    setResult(journalLines);
                    setReversalsResult(reversalJournalLines);
                }
                catch (ex) {
                    setError(ErrorUtils.getMessage(ex));
                }
                finally {
                    setLoading(false);
                }
            }
            if (calculationExecutionId != null) {
                fetchData(this, calculationExecutionId);
            }
        }, [calculationExecutionId]);
        return [result, reversalsResult, loading, error]
    }

    getJournalEntriesV2(calculationExecutionId?: string) : [JournalLine[], JournalLine[], boolean, string] {
        const [result, setResult] = useState([] as JournalLine[]);
        const [reversalsResult, setReversalsResult] = useState([] as JournalLine[]);
        const [loading, setLoading] = useState(false);
        const [error, setError] = useState('');

        useEffect(() => {
            async function fetchData($this: JournalsService, calculationExecutionId: string) {
                try {
                    setLoading(true);
                    const response = await apiService.getJournalEntriesV2(calculationExecutionId);
                    const json = await response.json();

                    // Fetch data from S3 URL
                    const s3Response = await fetch(json.s3DownloadUrl);
                    const journalRecord = await s3Response.json();
                    const journalLines = journalRecord.journalLines || [];
                    const reversalJournalLines = journalRecord.reversalJournalLines || [];
                    $this.convertJournalLineFieldsToDisplayFormat(journalLines);
                    $this.convertJournalLineFieldsToDisplayFormat(reversalJournalLines);
                    setResult(journalLines);
                    setReversalsResult(reversalJournalLines);
                }
                catch (ex) {
                    setError(ErrorUtils.getMessage(ex));
                }
                finally {
                    setLoading(false);
                }
            }
            if (calculationExecutionId != null) {
                fetchData(this, calculationExecutionId);
            }
        }, [calculationExecutionId]);
        return [result, reversalsResult, loading, error]
    }

    convertJournalFieldsToDisplayFormat(journals: Journal[]) {
        journals.forEach((journal: Journal) => {
            this.convertJournalLineFieldsToDisplayFormat(journal.journalLines);
        })
    }

    convertJournalLineFieldsToDisplayFormat(journalLines: JournalLine[]) {
        const formattingService: FormattingService = new FormattingService();
        journalLines.forEach((journalLine: JournalLine) => {
            journalLine.accountingDateDisplay = journalLine.accountingDate != null ? this.getUTCDateDisplay(journalLine.accountingDate) : "";
            journalLine.currencyConversionDateDisplay = journalLine.currencyConversionDate != null ? this.getUTCDateDisplay(journalLine.currencyConversionDate) : "";
            journalLine.reversalPeriodDisplay = journalLine.reversalPeriod != null ? format(new Date(journalLine.reversalPeriod), "MMM-yyyy") : "";
            journalLine.enteredDrFormatted = journalLine.enteredDr != null ? formattingService.formatString(journalLine.enteredDr) : "";
            journalLine.enteredCrFormatted = journalLine.enteredCr != null ? formattingService.formatString(journalLine.enteredCr) : "";
            journalLine.accountedDrFormatted = journalLine.accountedDr != null ? formattingService.formatString(journalLine.accountedDr) : "";
            journalLine.accountedCrFormatted = journalLine.accountedCr != null ? formattingService.formatString(journalLine.accountedCr) : "";
        })
    }

    getUTCDateDisplay(epoch: number) {
        const d = new Date(epoch);
        const utcDate = d.toUTCString();
        return (utcDate.substring(5, 16) as any).replaceAll(' ', '-');
    }

    convertLineErrorsToMap(journals: Journal[]) {
        journals.forEach((journal: Journal) => {
            journal.journalLines.forEach((journalLine: JournalLine) => {
                if (journalLine.errors != null) {
                    const errorMap = new Map<string, string>();
                    Object.keys(journalLine.errors).forEach(k => {
                        const mappedKey = JE_ERROR_FIELD_MAPPING[k] || k;
                        errorMap.set(mappedKey, (journalLine.errors as any)[k].join(', '));
                    })
                    journalLine.errors = errorMap;
                }
            })
        })
    }
 }